import CryptoJS from "crypto-js"
import { getCurrentConfig, PHONEPE_ENDPOINTS, REDIRECT_URLS, PAYMENT_STATUS } from "../constants/phonepe"
import { ordersService } from "./firebase"

export interface PaymentRequest {
  merchantTransactionId: string
  amount: number // in paise
  merchantUserId: string
  redirectUrl: string
  redirectMode: "POST" | "REDIRECT"
  callbackUrl?: string
  mobileNumber?: string
  paymentInstrument: {
    type: "PAY_PAGE" | "UPI_COLLECT" | "UPI_INTENT"
    upiId?: string
  }
}

export interface PaymentResponse {
  success: boolean
  code: string
  message: string
  data?: {
    merchantId: string
    merchantTransactionId: string
    transactionId: string
    amount: number
    state: string
    responseCode: string
    paymentInstrument?: {
      type: string
      utr?: string
      cardType?: string
      pgTransactionId?: string
    }
  }
}

export interface RefundRequest {
  merchantTransactionId: string
  originalTransactionId: string
  amount: number
  reason?: string
}

class PhonePeService {
  private config = getCurrentConfig()

  // Generate checksum for API requests
  private generateChecksum(payload: string, endpoint: string): string {
    const string = payload + endpoint + this.config.SALT_KEY
    const sha256 = CryptoJS.SHA256(string).toString()
    return sha256 + "###" + this.config.SALT_INDEX
  }

  // Verify webhook signature
  verifyWebhookSignature(payload: string, receivedChecksum: string): boolean {
    try {
      const [checksum] = receivedChecksum.split("###")
      const expectedChecksum = CryptoJS.SHA256(payload + this.config.SALT_KEY).toString()
      return checksum === expectedChecksum
    } catch (error) {
      console.error("Checksum verification failed:", error)
      return false
    }
  }

  // Create payment request
  async initiatePayment(paymentData: {
    orderId: string
    amount: number
    userId: string
    mobileNumber?: string
    paymentMode?: string
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const merchantTransactionId = `TXN_${paymentData.orderId}_${Date.now()}`

      const paymentRequest: PaymentRequest = {
        merchantTransactionId,
        amount: Math.round(paymentData.amount * 100), // Convert to paise
        merchantUserId: paymentData.userId,
        redirectUrl: REDIRECT_URLS.SUCCESS,
        redirectMode: "REDIRECT",
        callbackUrl: REDIRECT_URLS.CALLBACK,
        mobileNumber: paymentData.mobileNumber,
        paymentInstrument: {
          type: "PAY_PAGE",
        },
      }

      const payload = JSON.stringify(paymentRequest)
      const base64Payload = btoa(payload)
      const checksum = this.generateChecksum(base64Payload, PHONEPE_ENDPOINTS.PAY)

      const response = await fetch(`${this.config.BASE_URL}${PHONEPE_ENDPOINTS.PAY}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-VERIFY": checksum,
          "X-MERCHANT-ID": this.config.MERCHANT_ID,
          Accept: "application/json",
        },
        body: JSON.stringify({
          request: base64Payload,
        }),
      })

      const result = await response.json()

      if (result.success && result.data?.instrumentResponse?.redirectInfo?.url) {
        // Store payment initiation in Firebase
        await this.storePaymentRecord({
          merchantTransactionId,
          orderId: paymentData.orderId,
          userId: paymentData.userId,
          amount: paymentData.amount,
          status: PAYMENT_STATUS.PENDING,
          createdAt: new Date().toISOString(),
        })

        return {
          success: true,
          data: {
            paymentUrl: result.data.instrumentResponse.redirectInfo.url,
            merchantTransactionId,
            transactionId: result.data.transactionId,
          },
        }
      }

      return {
        success: false,
        error: result.message || "Payment initiation failed",
      }
    } catch (error) {
      console.error("Payment initiation error:", error)
      return {
        success: false,
        error: "Payment service unavailable",
      }
    }
  }

  // Check payment status
  async checkPaymentStatus(merchantTransactionId: string): Promise<PaymentResponse> {
    try {
      const endpoint = `${PHONEPE_ENDPOINTS.STATUS}/${this.config.MERCHANT_ID}/${merchantTransactionId}`
      const checksum = this.generateChecksum("", endpoint)

      const response = await fetch(`${this.config.BASE_URL}${endpoint}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-VERIFY": checksum,
          "X-MERCHANT-ID": this.config.MERCHANT_ID,
          Accept: "application/json",
        },
      })

      const result = await response.json()

      // Update payment status in Firebase
      if (result.success && result.data) {
        await this.updatePaymentStatus(merchantTransactionId, {
          status: result.data.state,
          transactionId: result.data.transactionId,
          responseCode: result.data.responseCode,
          paymentInstrument: result.data.paymentInstrument,
          updatedAt: new Date().toISOString(),
        })

        // Update order status based on payment status
        if (result.data.state === "COMPLETED") {
          await this.handleSuccessfulPayment(merchantTransactionId, result.data)
        } else if (result.data.state === "FAILED") {
          await this.handleFailedPayment(merchantTransactionId, result.data)
        }
      }

      return result
    } catch (error) {
      console.error("Status check error:", error)
      return {
        success: false,
        code: "INTERNAL_SERVER_ERROR",
        message: "Status check failed",
      }
    }
  }

  // Initiate refund
  async initiateRefund(refundData: RefundRequest): Promise<PaymentResponse> {
    try {
      const payload = JSON.stringify({
        merchantId: this.config.MERCHANT_ID,
        merchantTransactionId: refundData.merchantTransactionId,
        originalTransactionId: refundData.originalTransactionId,
        amount: Math.round(refundData.amount * 100), // Convert to paise
        callbackUrl: REDIRECT_URLS.CALLBACK,
      })

      const base64Payload = btoa(payload)
      const checksum = this.generateChecksum(base64Payload, PHONEPE_ENDPOINTS.REFUND)

      const response = await fetch(`${this.config.BASE_URL}${PHONEPE_ENDPOINTS.REFUND}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-VERIFY": checksum,
          "X-MERCHANT-ID": this.config.MERCHANT_ID,
          Accept: "application/json",
        },
        body: JSON.stringify({
          request: base64Payload,
        }),
      })

      const result = await response.json()

      // Store refund record in Firebase
      if (result.success) {
        await this.storeRefundRecord({
          merchantTransactionId: refundData.merchantTransactionId,
          originalTransactionId: refundData.originalTransactionId,
          amount: refundData.amount,
          reason: refundData.reason,
          status: "INITIATED",
          createdAt: new Date().toISOString(),
        })
      }

      return result
    } catch (error) {
      console.error("Refund initiation error:", error)
      return {
        success: false,
        code: "INTERNAL_SERVER_ERROR",
        message: "Refund initiation failed",
      }
    }
  }

  // Store payment record in Firebase
  private async storePaymentRecord(paymentData: any): Promise<void> {
    try {
      await ordersService.addPaymentRecord(paymentData)
    } catch (error) {
      console.error("Error storing payment record:", error)
    }
  }

  // Update payment status in Firebase
  private async updatePaymentStatus(merchantTransactionId: string, updateData: any): Promise<void> {
    try {
      await ordersService.updatePaymentRecord(merchantTransactionId, updateData)
    } catch (error) {
      console.error("Error updating payment status:", error)
    }
  }

  // Handle successful payment
  private async handleSuccessfulPayment(merchantTransactionId: string, paymentData: any): Promise<void> {
    try {
      // Get payment record to find order ID
      const paymentRecord = await ordersService.getPaymentRecord(merchantTransactionId)
      if (paymentRecord) {
        // Update order status
        await ordersService.updateOrderPaymentStatus(paymentRecord.orderId, {
          paymentStatus: "completed",
          paymentId: paymentData.transactionId,
          merchantTransactionId,
          paymentMethod: "phonepe",
          paymentDetails: paymentData,
        })

        // Update revenue analytics
        await this.updateRevenueAnalytics(paymentRecord.amount, paymentRecord.orderId)
      }
    } catch (error) {
      console.error("Error handling successful payment:", error)
    }
  }

  // Handle failed payment
  private async handleFailedPayment(merchantTransactionId: string, paymentData: any): Promise<void> {
    try {
      const paymentRecord = await ordersService.getPaymentRecord(merchantTransactionId)
      if (paymentRecord) {
        await ordersService.updateOrderPaymentStatus(paymentRecord.orderId, {
          paymentStatus: "failed",
          paymentMethod: "phonepe",
          paymentDetails: paymentData,
        })
      }
    } catch (error) {
      console.error("Error handling failed payment:", error)
    }
  }

  // Update revenue analytics
  private async updateRevenueAnalytics(amount: number, orderId: string): Promise<void> {
    try {
      const today = new Date().toISOString().split("T")[0]
      const month = new Date().toISOString().slice(0, 7)

      // Update daily revenue
      await ordersService.updateRevenueAnalytics("daily", today, amount)

      // Update monthly revenue
      await ordersService.updateRevenueAnalytics("monthly", month, amount)

      // Update total revenue
      await ordersService.updateRevenueAnalytics("total", "all-time", amount)
    } catch (error) {
      console.error("Error updating revenue analytics:", error)
    }
  }

  // Store refund record
  private async storeRefundRecord(refundData: any): Promise<void> {
    try {
      await ordersService.addRefundRecord(refundData)
    } catch (error) {
      console.error("Error storing refund record:", error)
    }
  }

  // Parse payment response from webhook
  parsePaymentResponse(base64Response: string): any {
    try {
      const decodedResponse = atob(base64Response)
      return JSON.parse(decodedResponse)
    } catch (error) {
      console.error("Error parsing payment response:", error)
      return null
    }
  }
}

export const phonePeService = new PhonePeService()
